import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Button } from 'antd';
import { connect } from 'react-redux';
import { Outlet } from 'react-router-dom';
import DashboardContext from '@/contexts/DashboardContext';
import ParamsCardContainer from '@/components/Dashboard/ParamsCard/ParamsCardContainer';
import ExpandedParamsCardProviderWrapper from '@/components/Dashboard/ExpandedParamsCardView/ExpandedParamsCardProviderWrapper';
import { DashboardDataProvider } from '@/components/Dashboard/DashboardDataContext';
import ModalExit from '@/components/ModalExit';
import ModalKeplerAnalysisHistorial from '@/components/Simulations/ModalKeplerAnalysisHistorial';
import SewerAnalysisLauncherModal from '@/components/Simulations/SewerAnalysisLauncherModal';
import SidePanel from '@/components/Dashboard/SidePanel/SidePanel';
import packageJson from '../../../package.json';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import HydrodigitalLogo from '@/components/General/HydrodigitalLogo';

import DashboardHeader from './DashboardHeader';
import Notifications from './Notifications';
import { useDashboardState } from './useDashboardState';
import { DashboardLayoutProps } from './constants';
import { InflowInfiltrationWarningModal } from '@/components/Dashboard/InflowInfiltration';
import DmaModeWarningModal from '@/components/Dashboard/DmaModeWarningModal';
import NotificationService from '@/domain/Notifications/NotificationService';
import { dashboardMapEventsService } from '@/events/dashboardMap.subject';
import './index.scss';

dayjs.extend(customParseFormat);

const { Content } = Layout;
const appVersion = packageJson.version;

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
	activities,
	security,
	readingsList,
	resumeList,
}) => {
	const {
		// State
		contextValue,
		setContextValue,
		openBlockDialog,
		setOpenBlockDialog,
		openHydraulicProfilerDialog,
		setOpenHydraulicProfilerDialog,
		showInputCalendar,
		setShowInputCalendar,
		showCalendar,
		setShowCalendar,
		dates,
		setDates,
		intervalRefresh,
		setIntervalRefresh,
		selectedPresetLabel,
		setSelectedPresetLabel,
		locations,
		setLocations,
		showLogoutModal,
		setShowLogoutModal,
		startDate,
		setStartDate,
		endDate,
		setEndDate,
		selectedTime1,
		setSelectedTime1,
		selectedTime2,
		setSelectedTime2,
		selectionPhase,
		setSelectionPhase,
		isOpen,
		setIsOpen,
		handleToggle,
		setHandleToggle,
		showKeplerAnalysisModal,
		setShowKeplerAnalysisModal,
		showSewerAnalysisLauncherModal,
		setShowSewerAnalysisLauncherModal,
		messageModal,
		setMessageModal,
		// Handlers
		disabledDate,
		handleChangeInterval,
		handleOpenBlockDialogChange,
		handleClickSelect,
		toggle,
		handleChangeDate,
		handleCalendarClick,
		handleSearch,
		handleTime1Change,
		handleTime2Change,
		openModalKeplerAnalysisHistorial,
		handleReset,
		handleSelectModule,
		
		// Navigation and dispatch
		navigate,
		location,
		dispatch,
	} = useDashboardState();

	const companyName = security.organizationName;
	const name = `${security.firstname} ${security.lastname}`;
	
	// State for Inflow warning modal
	const [showInflowWarningModal, setShowInflowWarningModal] = useState(false);
	const [showDmaWarningModal, setShowDmaWarningModal] = useState(false);
	const [latestStartedSewerAnalysisId, setLatestStartedSewerAnalysisId] = useState<string | null>(null);
	const [latestStartedSewerAnalysisName, setLatestStartedSewerAnalysisName] = useState<string | null>(null);
	const [sewerAnalysisRefreshKey, setSewerAnalysisRefreshKey] = useState(0);
	const [shouldPollSewerHistorical, setShouldPollSewerHistorical] = useState(false);

	// Load notification count for bell icon badge when location changes
	useEffect(() => {
		const locationId = contextValue.selectedLocation;
		if (!locationId) return;
		const svc = new NotificationService();
		svc.getRecentNotifications({ locationId, page: 1, kind: 'ALL' })
			.then((res) => {
				setContextValue((prev: any) => ({ ...prev, notificationCount: res?.totalRecords ?? 0 }));
			})
			.catch(() => {});
	}, [contextValue.selectedLocation]);

	const handleBackNavigation = () => {
		switch (editRouteType) {
			case 'stateMachines':
				navigate('/stateMachines');
				break;
			case 'diagramBlock':
				navigate('/block/diagram');
				break;
			default:
				navigate('/');
		}
	};

	const getEditRouteType = () => {
		if (location.pathname.startsWith('/stateMachines/edit')) {
			return 'stateMachines';
		} else if (
			location.pathname.includes('/diagram-block/') &&
			new URLSearchParams(location.search).get('isEdit') === 'true'
		) {
			return 'diagramBlock';
		}
		return null;
	};

	const editRouteType = getEditRouteType();
	const isEditMode = editRouteType !== null;

	const getBackButtonText = () => {
		switch (editRouteType) {
			case 'stateMachines':
				return 'Back to Smart Controls';
			case 'diagramBlock':
				return 'Back to Block Diagrams';
			default:
				return 'Back';
		}
	};

	const selectedNetworkType = useMemo(() => {
		if (!locations?.length || !contextValue.selectedLocation) return null;

		const findLocationRecursively = (items: any[]): any => {
			for (const item of items) {
				if (item?.locationId === contextValue.selectedLocation) return item;
				if (item?.childLocations?.length) {
					const found = findLocationRecursively(item.childLocations);
					if (found) return found;
				}
			}
			return null;
		};

		return findLocationRecursively(locations)?.networkType ?? null;
	}, [locations, contextValue.selectedLocation]);

	return (
		<DashboardContext.Provider
			value={{
				value: contextValue,
				setValue: setContextValue,
			}}
		>
			<Layout style={{ minHeight: '100vh' }}>
				<Layout className="site-layout" style={{ background: 'white', overflowY: 'auto' }}>
					<Content style={{ position: 'relative', overflow: 'hidden' }}>
						{isEditMode ? (
							<div className="header-interactiveSimulation">
								<HydrodigitalLogo onClick={() => {}} size="lg" />
								<div className="vertical-separator" />
								<Button
									color="primary"
									variant="outlined"
									shape="round"
									onClick={handleBackNavigation}
								>
									{getBackButtonText()}
								</Button>
							</div>
						) : (
							<DashboardHeader
								companyName={companyName}
								name={name}
								activitiesCount={activities.length}
								security={security}
								contextValue={contextValue}
								setContextValue={setContextValue}
								showCalendar={showCalendar}
								setShowCalendar={setShowCalendar}
								dates={dates}
								setDates={setDates}
								setSelectedTime1={setSelectedTime1}
								setSelectedTime2={setSelectedTime2}
								setSelectionPhase={setSelectionPhase}
								setShowLogoutModal={setShowLogoutModal}
								intervalRefresh={intervalRefresh}
								handleClickSelect={handleClickSelect}
								handleChangeInterval={handleChangeInterval}
								openModalKeplerAnalysisHistorial={openModalKeplerAnalysisHistorial}
								handleSelectModule={handleSelectModule}
								toggle={toggle}
								navigate={navigate}
								dispatch={dispatch}
								location={location}
								locations={locations}
								setShowInflowWarningModal={setShowInflowWarningModal}
								setShowDmaWarningModal={setShowDmaWarningModal}
							/>
						)}
						<p className="version">
							V<span>{appVersion}</span>
						</p>
						<Outlet />
					</Content>
					{location.pathname == '/' && (
						<>
							<DashboardDataProvider>
								<ExpandedParamsCardProviderWrapper
									readingsList={readingsList}
									resumeList={resumeList}
								/>
							</DashboardDataProvider>
						</>
					)}
				</Layout>
				{contextValue.showNotifications && <Notifications />}
			</Layout>
			{showLogoutModal && <ModalExit closeModal={setShowLogoutModal} />}

			<SidePanel
				visible={showSewerAnalysisLauncherModal}
				title={String(selectedNetworkType ?? '').toUpperCase() === 'WATER' ? 'Water Analysis' : 'Sewer Analysis'}
				subtitle="View list of previous analysis or run a new one"
				width={420}
				onClose={() => {
					setLatestStartedSewerAnalysisId(null);
					setLatestStartedSewerAnalysisName(null);
					setSewerAnalysisRefreshKey(0);
					setShouldPollSewerHistorical(false);
					setShowSewerAnalysisLauncherModal(false);
					setContextValue((prev: any) => ({
						...prev,
						activatedSewerAnalysisMode: false,
					}));
					dashboardMapEventsService.setSubject({ type: 'closePopup' });
					dashboardMapEventsService.setSubject({ type: 'setMode', data: 'default' });
				}}
			>
				<SewerAnalysisLauncherModal
					mode="panel"
					open={showSewerAnalysisLauncherModal}
					selectedLocation={contextValue.selectedLocation}
					selectedNetworkType={selectedNetworkType}
					trackedAnalysisId={latestStartedSewerAnalysisId}
					trackedAnalysisName={latestStartedSewerAnalysisName}
					refreshKey={sewerAnalysisRefreshKey}
					shouldPollHistorical={shouldPollSewerHistorical}
					onCancel={() => {
						setLatestStartedSewerAnalysisId(null);
						setLatestStartedSewerAnalysisName(null);
						setSewerAnalysisRefreshKey(0);
						setShouldPollSewerHistorical(false);
						setShowSewerAnalysisLauncherModal(false);
						setContextValue((prev: any) => ({
							...prev,
							activatedSewerAnalysisMode: false,
						}));
						dashboardMapEventsService.setSubject({ type: 'closePopup' });
						dashboardMapEventsService.setSubject({ type: 'setMode', data: 'default' });
					}}
					onCreateNewAnalysis={() => {
						setShowKeplerAnalysisModal(true);
					}}
				/>
			</SidePanel>

			<ModalKeplerAnalysisHistorial
				showKeplerAnalysisModal={showKeplerAnalysisModal}
				onCancel={() => {
					setShowKeplerAnalysisModal(false);
				}}
				selectedLocation={contextValue.selectedLocation}
				selectedNetworkType={selectedNetworkType}
				onSuccess={(response) => {
					const nextAnalysisId =
						response?.id ??
						response?.modelId ??
						response?.analysisId ??
						response?.iterationId ??
						response?.data?.id ??
						response?.data?.modelId ??
						response?.data?.analysisId ??
						response?.data?.iterationId ??
						null;
					const nextAnalysisName =
						response?.selectedModelName ?? response?.modelName ?? response?.name ?? null;
					setLatestStartedSewerAnalysisId(nextAnalysisId != null ? String(nextAnalysisId) : null);
					setLatestStartedSewerAnalysisName(
						nextAnalysisName != null ? String(nextAnalysisName) : null,
					);
					setSewerAnalysisRefreshKey((current) => current + 1);
					setShouldPollSewerHistorical(true);
					setShowSewerAnalysisLauncherModal(true);
				}}
				contextValue={contextValue}
			/>
			
			{/* Wet Weather Stress Warning Modal */}
			<InflowInfiltrationWarningModal
				visible={showInflowWarningModal}
				onClose={() => setShowInflowWarningModal(false)}
			/>
			<DmaModeWarningModal
				visible={showDmaWarningModal}
				onClose={() => setShowDmaWarningModal(false)}
			/>
		</DashboardContext.Provider>
	);
};

function mapState(state: any) {
	return {
		readingsList: state.readingsResumes.readings,
		resumeList: state.readingsResumes.resumes,
		activities: state.activities.array,
		security: state.security.array,
	};
}

export default connect(mapState)(DashboardLayout);
